// @file Surface comments store
import { trackEvent } from '@@/bits/analytics'
import { isAppCapableOf } from '@@/bits/app_can'
import { CONFIRM_MODERATION_REJECT_COMMENT, SELF_HARM_COMMENT_ALERT } from '@@/bits/confirmation_dialog'
import device from '@@/bits/device'
import { captureFetchException } from '@@/bits/error_tracker'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { navigateTo } from '@@/bits/location'
import { COMMENT_BODY_CHAR_LIMIT } from '@@/bits/numbers'
import { getVuexStore } from '@@/bits/pinia'
import { findLast } from '@@/bits/set'
import { ACCESS_WITHDRAWN } from '@@/bits/snackbar_helper'
import type { UploadJob } from '@@/bits/uploader'
import { selfHarmHelplineUrl } from '@@/bits/url'
import { vDel, vSet } from '@@/bits/vue'
import { CommentAttachmentState, SnackbarNotificationType } from '@@/enums'
import {
  OzConfirmationDialogBoxButtonScheme,
  useGlobalConfirmationDialogStore,
} from '@@/pinia/global_confirmation_dialog'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useNativeAppStore } from '@@/pinia/native_app'
import { useSurfaceCommentAttachmentsStore } from '@@/pinia/surface_comment_attachments'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { CommentApi, CommentV2Api } from '@@/surface/api/comment'
import type { Cid, Comment, CommentId, HashId, Id, Post, User } from '@@/types'
import type { CommentDraft } from '@@/types/surface_comments'
import type { RootState as SurfaceState } from '@@/vuexstore/surface/types'
import type { JsonAPIResource, JsonAPIResponse, Comment as V1Comment } from '@padlet/arvo'
import { cloneDeep, groupBy, isEqual, mapValues, omitBy, pickBy } from 'es-toolkit'
import { isEmpty } from 'es-toolkit/compat'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

export interface CreateCommentPayload {
  postId: Id
  htmlBody?: string
  attachment?: string
}

export const useCommentsStore = defineStore('comments', () => {
  const surfaceVuexStore = getVuexStore<SurfaceState>()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceCommentAttachments = useSurfaceCommentAttachmentsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()

  const isInitialCommentsLoadDone = ref<boolean>(false)
  const rawCommentEntities = ref<Record<CommentId, Comment>>({})
  const pendingCommentIds = ref<CommentId[]>([])
  const commentUnderActionId = ref<CommentId | null>(null)
  const activeNewCommentPostId = ref<Id | null>(null)
  const inlineCommentInputLastTypedInMs = ref<number>(0)
  const remoteComments = ref<Comment[]>([])

  const addPendingComment = (pendingComment: Comment): void => {
    rawCommentEntities.value[pendingComment.id] = pendingComment
    pendingCommentIds.value.push(pendingComment.id)
  }

  const removePendingComment = ({ commentId }: { commentId: CommentId }): void => {
    const isCommentPending = pendingCommentIds.value.includes(commentId)
    if (!isCommentPending) return
    pendingCommentIds.value = pendingCommentIds.value.filter((id) => id !== commentId)
    delete rawCommentEntities.value[commentId]
  }

  const user = computed<User>(() => surfaceVuexStore?.getters.user)

  const commentIds = computed<string[]>(() => {
    if (!isInitialCommentsLoadDone.value) return []
    return Object.keys(rawCommentEntities.value)
  })

  const hasComments = computed<boolean>(() => commentIds.value.length > 0)

  const commentsByPostId = computed<Record<Id, Comment[]>>(() => {
    if (!isInitialCommentsLoadDone.value) return {}
    return groupBy(Object.values(rawCommentEntities.value), (comment) => comment.wish_id)
  })

  const commentCountByPostId = computed<Record<Id, number>>(() => {
    return mapValues(commentsByPostId.value, (comments) => comments.length)
  })

  const commentIdsByPost = computed<{ [wishId: number]: CommentId[] }>(() => {
    if (!isInitialCommentsLoadDone.value) return {}
    return mapValues(commentsByPostId.value, (comments) => comments.map((comment) => comment.id))
  })

  // If the CommentId array for a post does not change,
  // reuse the same array instead of creating a new one with the same contents.
  // Use this to prevent unnecessary re-renders.
  const stableCommentIdsByPostId = ref<Record<Id, CommentId[]>>({})
  watch(
    commentIdsByPost,
    (newValue) => {
      const currentStableIds = stableCommentIdsByPostId.value
      const result = {}
      Object.keys(newValue).forEach((key) => {
        const oldIds = currentStableIds[key]
        const newIds = newValue[key]
        result[key] = isEqual(oldIds, newIds) ? oldIds : newIds
      })
      stableCommentIdsByPostId.value = result
    },
    { immediate: true },
  )

  const isCommenting = computed<boolean>(() => hasCommentBeingEdited.value || activeNewCommentPostId.value != null)

  const commentEntities = computed<Record<Id, Comment>>(() => {
    // Return unmodified comments if not viewing translations or translated data is missing
    if (
      !surfaceTranslationsStore.isViewingTranslatedPadlet ||
      surfaceTranslationsStore.translatedWall?.comments == null
    ) {
      return rawCommentEntities.value
    }

    // Create a map of translated comment bodies for efficient lookup
    const translatedBodies: Record<Id, string> = surfaceTranslationsStore.translatedWall.comments.reduce<
      Record<Id, string>
    >((map, translatedComment) => {
      map[translatedComment.commentId] = translatedComment.htmlBody
      return map
    }, {})

    // Iterate over raw comments and merge translated bodies if available
    return Object.entries(rawCommentEntities.value).reduce<Record<Id, Comment>>((acc, [commentId, comment]) => {
      const translatedBody = translatedBodies[commentId]
      acc[commentId] = {
        ...comment,
        // Only override html_body if a translation exists
        ...(translatedBody !== undefined && { html_body: translatedBody }),
      }
      return acc
    }, {})
  })

  const unapprovedComments = computed<Comment[]>(() => {
    const comments: Comment[] = Object.values(commentEntities.value)
    return comments.filter((comment) => comment.is_awaiting_approval === true)
  })
  const isAddingNewComment = computed(() => activeNewCommentPostId.value != null)

  /**
   * A set of comment IDs that are currently being edited.
   */
  const commentsBeingEditedSet = ref<Set<CommentId>>(new Set())

  const hasCommentBeingEdited = computed(() => {
    return commentsBeingEditedSet.value.size > 0
  })

  const isCommentBeingEdited = (commentId: CommentId): boolean => {
    return commentsBeingEditedSet.value.has(commentId)
  }

  const latestCommentBeingEdited = computed(() => findLast(commentsBeingEditedSet.value))

  // #region NEW/DRAFT COMMENTS

  const newCommentByPostCid = ref<Record<Cid, CommentDraft>>({})

  const updateNewCommentByPostCid = (postCid: Cid, comment: Partial<CommentDraft>): void => {
    const existingComment = newCommentByPostCid.value[postCid]
    if (existingComment == null && isEmpty(comment)) return
    vSet(newCommentByPostCid.value, postCid, {
      ...(existingComment ?? {}),
      ...comment,
    })
  }

  const deleteNewCommentProperties = (postCid: Cid, props: Array<keyof CommentDraft>): void => {
    const existingComment = newCommentByPostCid.value[postCid]
    if (existingComment == null) return

    const newComment = cloneDeep(existingComment)
    props.forEach((key) => {
      delete newComment[key]
    })

    // If the object is now empty, delete it from the `newCommentByPostCid` state.
    if (isEmpty(newComment)) {
      vDel(newCommentByPostCid.value, postCid)
    } else {
      vSet(newCommentByPostCid.value, postCid, newComment)
    }
  }

  const updateNewCommentBody = ({ postCid, body }: { postCid: Cid; body: string }): void => {
    updateNewCommentByPostCid(postCid, { html_body: body })
  }

  const updateNewCommentAttachment = ({ postCid, attachment }: { postCid: Cid; attachment: string | null }): void => {
    if (attachment != null) {
      updateNewCommentByPostCid(postCid, { attachment })
    } else {
      deleteNewCommentProperties(postCid, ['attachment'])
    }
  }

  const updateNewCommentUploadingFile = ({
    postCid,
    file,
    uploadJob,
  }: {
    postCid: Cid
    file: File
    uploadJob: UploadJob
  }): void => {
    updateNewCommentByPostCid(postCid, { uploadingFile: file, uploadJob })
  }

  const updateNewCommentUploadingFileProgress = ({ postCid, progress }: { postCid: Cid; progress: number }): void => {
    if (newCommentByPostCid.value[postCid]?.uploadingFile == null) return
    updateNewCommentByPostCid(postCid, { uploadingProgress: progress })
  }

  const isUploadingAttachmentForNewComment = ({ postCid }: { postCid: Cid }): boolean =>
    newCommentByPostCid.value[postCid]?.uploadingFile != null

  /**
   * Takes action on this new comment.
   * - Cancels the upload job if there is one.
   * - Deletes all states related to uploading file.
   */
  const removeUploadingFileForNewComment = ({ postCid }: { postCid: Cid }): void => {
    newCommentByPostCid.value[postCid].uploadJob?.cancel()
    deleteNewCommentProperties(postCid, ['uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  /**
   * Removes all states related to attachment for this new comment.
   */
  const removeAttachmentForNewComment = ({ postCid }: { postCid: Cid }): void => {
    newCommentByPostCid.value[postCid]?.uploadJob?.cancel()
    deleteNewCommentProperties(postCid, ['attachment', 'uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  const newCommentBodyForPostCid = (postCid: Cid): string => {
    return newCommentByPostCid.value[postCid]?.html_body ?? ''
  }

  const newCommentAttachmentForPostCid = (postCid: Cid): string | null => {
    return newCommentByPostCid.value[postCid]?.attachment ?? null
  }

  const newCommentUploadingFileForPostCid = (postCid: Cid): File | null => {
    return newCommentByPostCid.value[postCid]?.uploadingFile ?? null
  }

  const newCommentUploadingProgressForPostCid = (postCid: Cid): number | null => {
    return newCommentByPostCid.value[postCid]?.uploadingProgress ?? null
  }

  const newCommentUploadJobForPostCid = (postCid: Cid): UploadJob | null => {
    return newCommentByPostCid.value[postCid]?.uploadJob ?? null
  }

  const resetComments = (): void => {
    newCommentByPostCid.value = {}
  }
  // #endregion

  // #region COMMENTS BEING EDITED
  const editedCommentById = ref<Record<CommentId, CommentDraft>>({})

  const updateEditedCommentById = (id: CommentId, comment: Partial<CommentDraft>): void => {
    const existingComment = editedCommentById.value[id]
    if (existingComment == null && isEmpty(comment)) return
    vSet(editedCommentById.value, id, {
      ...(existingComment ?? {}),
      ...comment,
    })
  }

  const deleteEditedCommentProperties = (commentId: CommentId, props: Array<keyof CommentDraft>): void => {
    const existingComment = editedCommentById.value[commentId]
    if (existingComment == null) return

    const newComment = cloneDeep(existingComment)
    props.forEach((key) => {
      delete newComment[key]
    })

    // If the object is now empty, delete it from the `editedCommentById` state.
    if (isEmpty(newComment)) {
      vDel(editedCommentById.value, commentId)
    } else {
      vSet(editedCommentById.value, commentId, newComment)
    }
  }

  function updateEditedDraftCommentBody(payload: { commentId: CommentId; body: string }): void {
    updateEditedCommentById(payload.commentId, { html_body: payload.body })
  }

  const updateEditedDraftCommentAttachment = (payload: { commentId: CommentId; attachment: string | null }): void => {
    if (payload.attachment != null) {
      updateEditedCommentById(payload.commentId, { attachment: payload.attachment })
    } else {
      deleteEditedCommentProperties(payload.commentId, ['attachment'])
    }
  }

  const updateEditedDraftCommentUploadingFile = ({
    commentId,
    file,
    uploadJob,
  }: {
    commentId: CommentId
    file: File
    uploadJob: UploadJob
  }): void => {
    updateEditedCommentById(commentId, { uploadingFile: file, uploadJob })
  }

  const updateEditedDraftCommentUploadingFileProgress = ({
    commentId,
    progress,
  }: {
    commentId: CommentId
    progress: number
  }): void => {
    if (editedCommentById.value[commentId]?.uploadingFile == null) return
    updateEditedCommentById(commentId, { uploadingProgress: progress })
  }

  /**
   * Takes action on this comment.
   * - Cancels the upload job if there is one.
   * - Deletes all states related to uploading file.
   */
  const removeUploadingFileForEditedComment = ({ commentId }: { commentId: CommentId }): void => {
    editedCommentById.value[commentId].uploadJob?.cancel()
    deleteEditedCommentProperties(commentId, ['uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  /**
   * Removes all states related to attachment for this comment.
   */
  const removeAttachmentForEditedComment = ({ commentId }: { commentId: CommentId }): void => {
    editedCommentById.value[commentId].uploadJob?.cancel()
    deleteEditedCommentProperties(commentId, ['attachment', 'uploadingFile', 'uploadingProgress', 'uploadJob'])
  }

  const isUploadingAttachmentForEditedComment = ({ commentId }: { commentId: CommentId }): boolean =>
    editedCommentById.value[commentId]?.uploadingFile != null

  const editedDraftCommentBodyFromId = (commentId: CommentId): string => {
    return editedCommentById.value[commentId]?.html_body ?? ''
  }

  const editedDraftCommentAttachmentFromId = (commentId: CommentId): string | null => {
    return editedCommentById.value[commentId]?.attachment ?? null
  }

  const editedDraftCommentUploadingFileFromId = (commentId: CommentId): File | null => {
    return editedCommentById.value[commentId]?.uploadingFile ?? null
  }

  const editedDraftCommentUploadingProgressFromId = (commentId: CommentId): number | null => {
    return editedCommentById.value[commentId]?.uploadingProgress ?? null
  }

  const editedDraftCommentUploadJobFromId = (commentId: CommentId): UploadJob | null => {
    return editedCommentById.value[commentId]?.uploadJob ?? null
  }

  async function createCommentV2({ postId, htmlBody, attachment }: CreateCommentPayload): Promise<Comment | undefined> {
    const post: Post | undefined = surfaceVuexStore?.getters['post/getPostByServerId'](postId)
    if (post == null) {
      void globalSnackbarStore.genericFetchError()
      return
    }

    // tempId will be used to identify the comment in the frontend until it is persisted,
    // after which, we will use the server-provided Id.
    const tempId = `new_${Date.now()}`

    const newComment = {
      wish_id: postId,
      html_body: htmlBody,
      attachment,
      user_id: user.value.id,
      user_hashid: user.value.hashid,
    }

    addPendingComment({ id: tempId, ...newComment } as Comment)

    try {
      // Delete the new comment from the store and reset the attachment state to IDLE.
      // - The new comment isn't published, it's not new anymore.
      // - Reverse the state back to IDLE so that new comments can be created again.
      deleteNewCommentProperties(post.cid, ['html_body', 'attachment'])
      surfaceCommentAttachments.updateNewCommentAttachmentState({
        postCid: post.cid,
        state: CommentAttachmentState.IDLE,
      })

      const response = await CommentV2Api.create(newComment, {
        apiVersion: 8,
      })
      const comment = (response.data as JsonAPIResource<Comment>).attributes

      removePendingComment({ commentId: tempId })
      rawCommentEntities.value[comment.id] = comment

      if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateCommentEntity(comment.id)
      }
      return comment
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceCreateCommentV2' })
      if (post == null || post.cid == null) {
        removePendingComment({ commentId: tempId })
      } else {
        const comment = rawCommentEntities.value[tempId]
        removePendingComment({ commentId: tempId })
        updateNewCommentBody({ postCid: post.cid, body: comment.html_body ?? comment.body })
        updateNewCommentAttachment({ postCid: post.cid, attachment: comment.attachment ?? null })
        return comment
      }
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
    return undefined
  }

  function startEditingCommentV2({ commentId }: { commentId: CommentId }): void {
    commentsBeingEditedSet.value.add(commentId)
    const editingComment = rawCommentEntities.value[commentId]
    updateEditedCommentById(commentId, {
      html_body: editingComment?.html_body,
      attachment: editingComment?.attachment,
    })
    if (editingComment?.attachment != null) {
      surfaceCommentAttachments.updateEditedCommentAttachmentState({ commentId, state: CommentAttachmentState.PREVIEW })
    }
  }

  async function editCommentV2({
    id,
    payload,
  }: {
    id: CommentId
    payload: { htmlBody?: string; attachment?: string | null }
  }): Promise<void> {
    // Comparing to existingComment so that only comments that already exceed character limit can continue to exceed character limit
    const existingCommentBody = rawCommentEntities.value[id]?.html_body ?? ''
    if (
      existingCommentBody.length <= COMMENT_BODY_CHAR_LIMIT &&
      payload.htmlBody != null &&
      payload.htmlBody.length > COMMENT_BODY_CHAR_LIMIT
    ) {
      showCommentCharacterLimitSnackbar()
      return
    }

    // Store original values for potential reversion on error
    const originalComment = { ...rawCommentEntities.value[id] }

    try {
      rawCommentEntities.value[id] = {
        ...rawCommentEntities.value[id],
        html_body: payload.htmlBody,
        attachment: payload.attachment,
      }

      const {
        data: {
          attributes: { html_body: newHtmlBody, body: newBody, attachment: newAttachment },
        },
      } = await CommentV2Api.update(id as number, payload, {
        apiVersion: 8,
      })

      rawCommentEntities.value[id] = {
        ...rawCommentEntities.value[id],
        html_body: newHtmlBody,
        body: newBody,
        attachment: newAttachment,
      }

      const shouldTranslateComment = existingCommentBody !== payload.htmlBody
      if (shouldTranslateComment && surfaceTranslationsStore.isViewingTranslatedPadlet) {
        void surfaceTranslationsStore.translateCommentEntity(id)
      }
      stopEditingComment(id)
    } catch (e) {
      // Revert optimistic update but preserve user's draft changes
      rawCommentEntities.value[id] = originalComment
      // Keep the user's changes in the draft state (preserve their failed edits)
      if (payload.htmlBody !== undefined) {
        updateEditedCommentById(id, { html_body: payload.htmlBody })
      }
      if (payload.attachment !== undefined) {
        updateEditedCommentById(id, { attachment: payload.attachment })
      }
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceEditCommentV2' })
    } finally {
      void surfaceVuexStore?.dispatch('realtime/dispatchAllQueuedActions', null, { root: true })
      void useNativeAppStore().postSurfaceState()
    }
  }

  function markCommentAsApproved(commentId: CommentId): void {
    rawCommentEntities.value[commentId].is_awaiting_approval = false
  }

  async function approveComment(comment: Comment): Promise<void> {
    await CommentV2Api.approve(Number(comment.id))
    markCommentAsApproved(comment.id)
  }

  async function rejectComment(comment: Comment): Promise<void> {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...CONFIRM_MODERATION_REJECT_COMMENT,
      afterConfirmActions: [
        async () => {
          delete rawCommentEntities.value[comment.id]
          await CommentApi.delete(comment)
        },
      ],
    })
  }

  function showCommentCharacterLimitSnackbar(): void {
    void globalSnackbarStore.setSnackbar({
      message: __('Comment character limit reached'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function askToDeleteComment({ commentId }: { commentId: Id }): void {
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      title: __('Delete this comment?'),
      body: __('Are you sure you want to delete this comment? This cannot be undone!'),
      confirmButtonText: __('Delete'),
      cancelButtonText: __('Cancel'),
      buttonScheme: OzConfirmationDialogBoxButtonScheme.Danger,
      afterConfirmActions: [async () => await deleteComment({ commentId })],
    })
  }
  // #endregion

  function showSelfHarmCommentAlert({ comment_id }: { comment_id?: Id }): void {
    if (comment_id == null) return

    trackEvent('SurfaceSelfHarmWarning', 'Showed Self-harm dialog for comment', comment_id)
    void useGlobalConfirmationDialogStore().openConfirmationDialog({
      ...SELF_HARM_COMMENT_ALERT,
      afterConfirmActions: [() => navigateTo(selfHarmHelplineUrl(), { target: 'blank' })],
    })
  }

  /* ---------------------- */
  /* UNSAVED CHANGES        */
  /* ---------------------- */

  const isAnyCommentInputFocused = ref<boolean>(false)

  const hasUnsavedCommentDrafts = computed((): boolean => {
    // Check if any new comment drafts have content
    const hasNewCommentDrafts = Object.values(newCommentByPostCid.value).some(
      (draft) => draft?.html_body && draft.html_body.trim().length > 0,
    )

    // Check if any edited comment drafts have content
    const hasEditedCommentDrafts = Object.values(editedCommentById.value).some(
      (draft) => draft?.html_body && draft.html_body.trim().length > 0,
    )

    return hasNewCommentDrafts || hasEditedCommentDrafts
  })

  const shouldShowUnsavedChangesDialog = computed((): boolean => {
    return hasUnsavedCommentDrafts.value && isAnyCommentInputFocused.value
  })

  function setIsAnyCommentInputFocused(focused: boolean): void {
    isAnyCommentInputFocused.value = focused
  }

  // #region Fetch Comments
  /* ---------------------- */
  /* COMMENTS FETCHING      */
  /* ---------------------- */

  const postsWhereCommentsAreFetched = ref<Record<Id, boolean>>({})
  const hasDispatchedCommentFetchForPost = (postId: Id): boolean => postsWhereCommentsAreFetched.value[postId] ?? false

  // Calls the appropriate API to fetch comments based on app capabilities and settings
  async function callFetchAPI({
    wishId,
    wishHashid,
    pageStart,
  }: {
    wishId?: Id
    wishHashid?: HashId
    pageStart?: string
  }): Promise<JsonAPIResponse<Comment | V1Comment>> {
    if (!surfaceVuexStore?.state.shouldFallbackWsDataFetchToRest && isAppUsing('realtimeFetching')) {
      const params = pageStart ? { wishId, wishHashid, pageStart } : { wishId, wishHashid }
      return await surfaceVuexStore?.dispatch('realtime/fetchComments', params, { root: true })
    }

    if (device.app && !isAppCapableOf('commentModeration')) {
      const params = wishId != null ? { wishId } : { wallId: surfaceVuexStore?.getters.wallId }
      if (pageStart) {
        params['pageStart'] = pageStart
      }
      return await CommentApi.fetch(params)
    }

    const params = wishHashid != null ? { wishHashid } : { wallHashid: surfaceVuexStore?.getters.wallHashid }
    if (pageStart) {
      params['pageStart'] = pageStart
    }
    return await CommentV2Api.fetch(params)
  }

  // shouldResetState is a flag to reset the stored comment data
  // currently it is only set to true for the initial comment load when a Padlet page is first loaded
  async function fetchComments({
    wishId,
    wishHashid,
    shouldResetState,
  }: {
    wishId?: Id
    wishHashid?: HashId
    shouldResetState?: boolean
  }): Promise<void> {
    let fetchedData
    try {
      fetchedData = await callFetchAPI({ wishId, wishHashid })
    } catch (e) {
      // Don't capture exception when it's 401, show a snackbar instead
      if (e.status === 401) {
        useGlobalSnackbarStore().setSnackbar(ACCESS_WITHDRAWN)
      } else {
        captureFetchException(e, { source: 'fetchComments' })
      }
      return
    }
    const { data, meta } = fetchedData
    const nextPageCursor = meta?.next
    fetchCommentsSuccess({
      newComments: data,
      wishId,
      isFirstPage: true,
      isLastPage: nextPageCursor == null || nextPageCursor === '',
      shouldResetState,
    })

    if (nextPageCursor != null && nextPageCursor !== '') {
      await fetchCommentsForPage({ wishId, wishHashid, pageStart: nextPageCursor })
    }

    if (wishId != null) {
      postsWhereCommentsAreFetched.value[wishId] = true
    }
  }

  async function fetchCommentsForPage({ wishId, wishHashid, pageStart }): Promise<void> {
    let commentPage
    try {
      commentPage = await callFetchAPI({ wishId, wishHashid, pageStart })
    } catch (e) {
      captureFetchException(e, { source: 'fetchCommentsForPage' })
      return
    }
    const { data, meta } = commentPage
    const nextPageCursor = meta?.next
    fetchCommentsSuccess({
      newComments: data,
      wishId,
      isFirstPage: meta?.isFirstPage,
      isLastPage: nextPageCursor == null || nextPageCursor === '',
      shouldResetState: false,
    })
    if (nextPageCursor != null && nextPageCursor !== '') {
      await fetchCommentsForPage({ wishId, wishHashid, pageStart: nextPageCursor })
    }
  }

  // During auto refresh, we will fetch the same page again
  // some data may have been deleted from the page but they are not synced by realtime and still exist in the page
  // we need to identify and delete them
  function idsToDeleteInPage(
    isFirstPage: boolean,
    isLastPage: boolean,
    newData: JsonAPIResource<Comment>[],
    existingEntitiesById: Record<string, Comment>,
  ): (string | number)[] {
    // the array returned from the back end is guaranteed to be ordered by id (DESC)
    const newIdList = newData.map((datum) => parseInt(datum.attributes.id as string))

    // last page should cover the smallest post id
    const lookupRangeStart = isLastPage ? 0 : newIdList[newData.length - 1]

    // first page should cover the biggest post id
    const lookupRangeEnd = isFirstPage ? Infinity : newIdList[0]

    return Object.keys(existingEntitiesById).filter((idKey: string): boolean => {
      const id: number = parseInt(existingEntitiesById[idKey].id as string)
      if (id > lookupRangeEnd || id < lookupRangeStart) {
        return false
      }

      // Anything that's inside the page's range but not in newIdList
      // means they are deleted in the backend but the frontend does not know yet
      return !newIdList.includes(id)
    })
  }

  // Helper to get the correct comment state from backend's data and what's currently in the frontend
  function reconcileCommentsAfterFetch({
    newComments,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }): Record<CommentId, Comment> {
    const isOnlyPage = isFirstPage && isLastPage
    const newCommentEntities = shouldResetState || isOnlyPage ? {} : { ...rawCommentEntities.value }

    // we are paginating the comment API by id so newComments from fetched data only contains 1 page
    // during auto refresh, if a comment id that's supposed to be in the current page exists in the current state but not in newComments means it has been deleted
    // => we have to remove it from newCommentEntities
    if (
      !shouldResetState && // we are only updating the state, i.e auto refresh or appending new page
      isInitialCommentsLoadDone.value && // Don't try to remove anything when the first page load is not done
      newComments.length
    ) {
      const idsToDelete = idsToDeleteInPage(isFirstPage, isLastPage, newComments, newCommentEntities)
      idsToDelete.forEach((id) => {
        delete newCommentEntities[id]
      })
    }

    newComments.forEach((commentData) => {
      const comment = commentData.attributes
      newCommentEntities[comment.id] = comment
    })

    return newCommentEntities
  }

  // Helper to get the correct comment state from backend's data and what's currently in the frontend
  function reconcileCommentsForPostAfterFetch({
    newComments,
    wishId,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }): Record<CommentId, Comment> {
    const isOnlyPage = isFirstPage && isLastPage
    const newCommentEntities =
      shouldResetState || isOnlyPage
        ? {}
        : (pickBy(rawCommentEntities.value, (comment) => comment.wish_id === wishId) as Record<CommentId, Comment>)

    // we are paginating the comment API by id so newComments from fetched data only contains 1 page
    // during auto refresh, if a comment id that's supposed to be in the current page exists in the current state but not in newComments means it has been deleted
    // => we have to remove it from newCommentEntities
    if (
      !shouldResetState && // we are only updating the state, i.e auto refresh or appending new page
      newComments.length
    ) {
      const idsToDelete = idsToDeleteInPage(isFirstPage, isLastPage, newComments, newCommentEntities)
      idsToDelete.forEach((id) => {
        delete newCommentEntities[id]
      })
    }

    newComments.forEach((commentData) => {
      const comment = commentData.attributes
      newCommentEntities[comment.id] = comment
    })

    return newCommentEntities
  }

  function fetchCommentsSuccess({
    newComments,
    wishId,
    isFirstPage,
    isLastPage,
    shouldResetState,
  }: {
    newComments: JsonAPIResource<Comment>[]
    wishId?: Id
    isFirstPage: boolean
    isLastPage: boolean
    shouldResetState?: boolean
  }): void {
    const newCommentEntities =
      wishId != null
        ? reconcileCommentsForPostAfterFetch({
            newComments,
            wishId,
            isFirstPage,
            isLastPage,
            shouldResetState,
          })
        : reconcileCommentsAfterFetch({
            newComments,
            isFirstPage,
            isLastPage,
            shouldResetState,
          })

    if (wishId == null) {
      rawCommentEntities.value = newCommentEntities
    } else {
      rawCommentEntities.value = {
        ...(omitBy(rawCommentEntities.value, (comment) => comment.wish_id === wishId) as Record<CommentId, Comment>),
        ...newCommentEntities,
      }
    }

    if (isLastPage) {
      isInitialCommentsLoadDone.value = true
      pendingCommentIds.value = []
    }

    void useNativeAppStore().postSurfaceState()
  }

  function refreshComments(payload: JsonAPIResponse<Comment>): void {
    const { data, meta } = payload
    if (!data || !Array.isArray(data)) return
    fetchCommentsSuccess({
      newComments: data,
      isFirstPage: meta?.isFirstPage,
      isLastPage: meta?.next == null || meta?.next === '',
      shouldResetState: false,
    })
  }

  // #endregion Fetch Comments

  // #region Vuex Actions

  /* ---------------------- */
  /* VUEX ACTIONS           */
  /* ---------------------- */

  function showCommentActionMenu({ commentId }: { commentId: CommentId }): void {
    if (commentUnderActionId.value) {
      commentUnderActionId.value = null
    } else {
      commentUnderActionId.value = commentId
    }
  }

  function hideCommentActionMenu(): void {
    commentUnderActionId.value = null
  }

  function stopEditingComment(commentId: CommentId): void {
    if (isCommentBeingEdited(commentId)) {
      vDel(editedCommentById.value, commentId)
      surfaceCommentAttachments.removeEditedCommentAttachmentState({ commentId })
    }
    commentsBeingEditedSet.value.delete(commentId)
  }

  function startWritingNewComment({ postId }: { postId: Id }): void {
    activeNewCommentPostId.value = postId
  }

  function stopWritingNewComment({ postId }: { postId?: Id }): void {
    if (activeNewCommentPostId.value === postId) {
      activeNewCommentPostId.value = null
    }
  }

  function resetWritingNewComment(payload: { post: Post }): void {
    deleteNewCommentProperties(payload.post.cid, ['html_body', 'attachment'])
    stopWritingNewComment({ postId: payload.post.id })
    surfaceCommentAttachments.removeNewCommentAttachmentState({ postCid: payload.post.cid })
  }

  async function deleteComment({ commentId }: { commentId: CommentId }): Promise<void> {
    const existingComment = rawCommentEntities.value[commentId]
    stopEditingComment(commentId)
    if (!existingComment) return
    try {
      delete rawCommentEntities.value[existingComment.id]
      await CommentApi.delete(existingComment)
    } catch (e) {
      void globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'SurfaceDeleteComment' })
      // Re-add the comment back to the store since deletion failed
      rawCommentEntities.value[existingComment.id] = existingComment
    }
    void useNativeAppStore().postSurfaceState()
  }

  function newCommentRemote({ comment }: { comment: Comment }): void {
    rawCommentEntities.value[comment.id] = comment
    remoteComments.value = [...remoteComments.value, comment]
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.translateCommentEntity(comment.id)
    }
    void useNativeAppStore().postSurfaceState()
  }

  function editCommentRemote({ comment }: { comment: Comment }): void {
    const existingComment = rawCommentEntities.value[comment.id]

    if (existingComment == null) {
      // For auto-moderated walls, a newly created comment is only published by realtime to the author.
      // Once it is auto-moderated, edit_comment is published to non-authors so we need to create the comment now.
      newCommentRemote({ comment })
      return
    }

    // real time can be out of sync sometimes.
    // make sure we are not updating a new copy with a stale one
    if (existingComment.updated_at > comment.updated_at) return

    rawCommentEntities.value[comment.id] = comment
    // Replace remoteComments with the newer comment
    remoteComments.value = remoteComments.value.map((c) => (c.id === comment.id ? comment : c))

    if (surfaceTranslationsStore.isViewingTranslatedPadlet && existingComment?.html_body !== comment.html_body) {
      void surfaceTranslationsStore.translateCommentEntity(comment.id)
    }

    void useNativeAppStore().postSurfaceState()
  }

  function deleteCommentRemote({ comment }: { comment: Comment }): void {
    if (isCommentBeingEdited(comment.id)) stopEditingComment(comment.id)
    if (commentUnderActionId.value === comment.id) {
      commentUnderActionId.value = null
    }
    delete rawCommentEntities.value[comment.id]
    void useNativeAppStore().postSurfaceState()

    remoteComments.value = remoteComments.value.filter((c) => c.id !== comment.id)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      void surfaceTranslationsStore.deleteCommentFromTranslatedWall(comment.id)
    }
  }

  // Update the timestamp where user last actively type in the inline comment input
  function updateInlineCommentInputLastTypedInMs(): void {
    inlineCommentInputLastTypedInMs.value = Date.now()
  }

  // #endregion Vuex Actions

  // #region Comments Panel
  /* ---------------------- */
  /* COMMENTS PANEL         */
  /* ---------------------- */
  const xCommentsPanel = computed<boolean>(() => surfaceVuexStore?.state.xCommentsPanel)
  const commentsPanelPostId = computed<Id>(() => surfaceVuexStore?.state.commentsPanelPostId)
  const hideCommentsPanel = (): void => {
    void surfaceVuexStore?.dispatch('hideCommentsPanel')
  }

  // #endregion Comments Panel

  return {
    hasComments,
    commentIds,
    commentIdsByPost,
    commentCountByPostId,
    stableCommentIdsByPostId,
    commentsByPostId,
    isCommenting,
    inlineCommentInputLastTypedInMs,
    commentEntities,
    commentUnderActionId,
    activeNewCommentPostId,
    xCommentsPanel,
    commentsPanelPostId,
    unapprovedComments,
    commentsBeingEditedSet,
    hasCommentBeingEdited,
    isCommentBeingEdited,
    remoteComments,
    hasDispatchedCommentFetchForPost,
    // New/draft comments
    newCommentByPostCid,
    isAddingNewComment,
    isUploadingAttachmentForNewComment,
    newCommentBodyForPostCid,
    newCommentAttachmentForPostCid,
    newCommentUploadingFileForPostCid,
    newCommentUploadingProgressForPostCid,
    resetComments,
    updateNewCommentBody,
    updateNewCommentAttachment,
    updateNewCommentUploadingFile,
    updateNewCommentUploadingFileProgress,
    removeUploadingFileForNewComment,
    removeAttachmentForNewComment,
    newCommentUploadJobForPostCid,
    // Comments being edited
    editedCommentById,
    latestCommentBeingEdited,
    isUploadingAttachmentForEditedComment,
    updateEditedDraftCommentBody,
    updateEditedDraftCommentAttachment,
    updateEditedDraftCommentUploadingFile,
    updateEditedDraftCommentUploadingFileProgress,
    removeUploadingFileForEditedComment,
    removeAttachmentForEditedComment,
    editedDraftCommentBodyFromId,
    editedDraftCommentAttachmentFromId,
    editedDraftCommentUploadingFileFromId,
    editedDraftCommentUploadingProgressFromId,
    editedDraftCommentUploadJobFromId,
    showCommentCharacterLimitSnackbar,

    // Actions
    createCommentV2,
    startEditingCommentV2,
    editCommentV2,
    markCommentAsApproved,
    approveComment,
    rejectComment,
    askToDeleteComment,
    fetchComments,
    refreshComments,
    showCommentActionMenu,
    hideCommentActionMenu,
    stopEditingComment,
    startWritingNewComment,
    stopWritingNewComment,
    resetWritingNewComment,
    deleteComment,
    showSelfHarmCommentAlert,
    newCommentRemote,
    editCommentRemote,
    deleteCommentRemote,
    updateInlineCommentInputLastTypedInMs,
    hideCommentsPanel,
    deleteNewCommentProperties,

    // Unsaved changes detection
    hasUnsavedCommentDrafts,
    shouldShowUnsavedChangesDialog,
    setIsAnyCommentInputFocused,
  }
})
